/**
 * EnergyKpiCards Component
 *
 * Displays 4 KPI cards for energy consumption summary:
 * - This Month kWh
 * - This Month Cost (inc VAT)
 * - Daily Average kWh
 * - Average Cost/Day
 */

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

function formatCurrency(value) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value)
}

function KpiCard({ title, value, subtitle, isLoading }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-2">
          <p className="text-sm font-medium text-neutral-600">{title}</p>
          {isLoading ? (
            <Skeleton className="h-10 w-32" />
          ) : (
            <p className="text-3xl font-bold text-neutral-900">{value}</p>
          )}
          {subtitle && (
            <p className="text-xs text-neutral-500">{subtitle}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default function EnergyKpiCards({ monthly, meters, isLoading }) {
  // Get current month data (latest entry)
  const currentMonth = monthly?.[monthly.length - 1]

  // Get the meter's VAT rate
  const vatRate = meters?.[0]?.vat_rate ?? 0.20

  // Calculate values
  const totalKwh = currentMonth ? parseFloat(currentMonth.total_kwh) : null
  const totalExclVat = currentMonth ? parseFloat(currentMonth.total_excl_vat_pence) / 100 : null
  const totalInclVat = totalExclVat !== null ? totalExclVat * (1 + vatRate) : null
  const daysWithData = currentMonth ? parseInt(currentMonth.days_with_data) : null
  const dailyAvgKwh = totalKwh !== null && daysWithData ? totalKwh / daysWithData : null
  const dailyAvgCost = totalInclVat !== null && daysWithData ? totalInclVat / daysWithData : null

  // Month label
  const monthLabel = currentMonth?.month
    ? new Date(currentMonth.month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : 'This Month'

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        title="Monthly Consumption"
        value={totalKwh !== null ? `${formatNumber(totalKwh, 0)} kWh` : '—'}
        subtitle={monthLabel}
        isLoading={isLoading}
      />
      <KpiCard
        title="Monthly Cost"
        value={formatCurrency(totalInclVat)}
        subtitle="Including VAT"
        isLoading={isLoading}
      />
      <KpiCard
        title="Daily Average"
        value={dailyAvgKwh !== null ? `${formatNumber(dailyAvgKwh, 1)} kWh` : '—'}
        subtitle={daysWithData ? `Based on ${daysWithData} days` : null}
        isLoading={isLoading}
      />
      <KpiCard
        title="Average Cost/Day"
        value={dailyAvgCost !== null ? formatCurrency(dailyAvgCost) : '—'}
        subtitle="Including VAT"
        isLoading={isLoading}
      />
    </div>
  )
}
